/**
 * ApiKeysPanel — Settings → API Keys (Josh 08/12: "connect the different
 * sites via API and have it do different things").
 *
 * Create named keys (full or read-only), copy the key ONCE, revoke anytime.
 * External systems send  X-API-Key: pfs_live_...  to /api endpoints.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { KeyRound, Copy, Ban } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ApiKeysPanel = ({ getAuthHeaders }) => {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState('');
  const [scope, setScope] = useState('readonly');
  const [creating, setCreating] = useState(false);
  const [freshKey, setFreshKey] = useState(null); // {name, api_key} — shown once

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api-keys`, { headers: getAuthHeaders() });
      setKeys(res.data.keys || []);
    } catch (e) {
      toast.error('Failed to load API keys');
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const createKey = async () => {
    if (!name.trim()) { toast.error('Give the key a name (e.g. "Zapier", "Website form")'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api-keys`, { name: name.trim(), scope }, { headers: getAuthHeaders() });
      setFreshKey(res.data);
      setName('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Anything using it stops working immediately.`)) return;
    try {
      await axios.post(`${API}/api-keys/${k.id}/revoke`, {}, { headers: getAuthHeaders() });
      toast.success(`Revoked ${k.name}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(freshKey.api_key);
    toast.success('Key copied to clipboard');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-5 w-5" />
          API Keys
        </CardTitle>
        <CardDescription>
          Connect external sites and tools to the CRM API. Send the header{' '}
          <code className="bg-gray-100 px-1 rounded">X-API-Key: pfs_live_…</code> on requests to{' '}
          <code className="bg-gray-100 px-1 rounded">/api/…</code> — full endpoint docs at{' '}
          <a className="underline" href={`${process.env.REACT_APP_BACKEND_URL}/docs`} target="_blank" rel="noreferrer">/docs</a>.
          Read-only keys can fetch data; full keys can do everything an admin can.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Create */}
        <div className="flex flex-wrap items-end gap-3 border rounded-md p-3 bg-slate-50">
          <div className="space-y-1">
            <Label>Key name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder='e.g. "Website", "Zapier"' className="w-56"
              data-testid="api-key-name-input" />
          </div>
          <div className="space-y-1">
            <Label>Access</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-40" data-testid="api-key-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="readonly">Read-only</SelectItem>
                <SelectItem value="full">Full access</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={createKey} disabled={creating} data-testid="api-key-create-btn">
            {creating ? 'Creating…' : 'Create Key'}
          </Button>
        </div>

        {/* Fresh key — shown once */}
        {freshKey && (
          <div className="border-2 border-emerald-300 bg-emerald-50 rounded-md p-3 space-y-2" data-testid="api-key-fresh">
            <div className="text-sm font-semibold text-emerald-900">
              "{freshKey.name}" created — copy this key NOW. It will never be shown again.
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white border rounded px-2 py-1.5 text-sm break-all">{freshKey.api_key}</code>
              <Button size="sm" variant="outline" onClick={copyKey} data-testid="api-key-copy-btn">
                <Copy className="h-4 w-4 mr-1" /> Copy
              </Button>
            </div>
            <button className="text-xs text-emerald-700 underline" onClick={() => setFreshKey(null)}>
              I've saved it — dismiss
            </button>
          </div>
        )}

        {/* List */}
        <div className="divide-y border rounded-md">
          {keys.length === 0 && (
            <div className="p-4 text-sm text-gray-500 text-center">No API keys yet.</div>
          )}
          {keys.map((k) => (
            <div key={k.id} className="flex items-center justify-between gap-3 p-3" data-testid={`api-key-row-${k.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium flex items-center gap-2">
                  {k.name}
                  <Badge variant={k.scope === 'full' ? 'destructive' : 'secondary'}>
                    {k.scope === 'full' ? 'Full access' : 'Read-only'}
                  </Badge>
                  {k.revoked && <Badge variant="outline" className="text-red-600 border-red-300">Revoked</Badge>}
                </div>
                <div className="text-xs text-gray-500 font-mono">
                  {k.prefix}…&nbsp;·&nbsp;created {String(k.created_at || '').slice(0, 10)}
                  {k.last_used_at ? ` · last used ${String(k.last_used_at).slice(0, 10)}` : ' · never used'}
                </div>
              </div>
              {!k.revoked && (
                <Button size="sm" variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => revoke(k)} data-testid={`api-key-revoke-${k.id}`}>
                  <Ban className="h-4 w-4 mr-1" /> Revoke
                </Button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ApiKeysPanel;
